import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { DocPicker } from "@/components/DocPicker";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitCompareArrows, Loader2, Sparkles, Download, Save } from "lucide-react";
import { AIText } from "@/components/AIText";

const PRIORITIES = [
  { v: "balanced", labelKey: "pr_balanced" },
  { v: "cost", labelKey: "pr_cost" },
  { v: "protection", labelKey: "pr_protection" },
  { v: "deductible", labelKey: "pr_deductible" },
  { v: "liability", labelKey: "pr_liability" },
  { v: "simplicity", labelKey: "pr_simplicity" },
];

export default function Compare() {
  const { t, lang } = useI18n();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [priority, setPriority] = useState("balanced");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!a || !b) { toast.error(t("select_two")); return; }
    if (a === b) { toast.error(t("choose_two_diff")); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/analysis/compare", { doc_a_id: a, doc_b_id: b, priority, language: lang });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Comparison failed"); }
    finally { setLoading(false); }
  };

  const exportPdf = () => downloadReport({
    title: "Policy Comparison", language: lang,
    subtitle: `${result.policy_a.name} vs. ${result.policy_b.name}`,
    sections: [{ heading: "Comparison", body: result.result }],
  });

  const saveCmp = async () => {
    try {
      await api.post("/saved-analyses", {
        kind: "comparison",
        title: `Comparison: ${result.policy_a.name} vs. ${result.policy_b.name}`,
        content: result.result, meta: { priority },
      });
      toast.success(t("saved_to_analyses"));
    } catch { toast.error(t("could_not_save")); }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><GitCompareArrows className="h-7 w-7 text-brandblue" /> {t("compare_title")}</h1>
      <p className="mt-1 text-slate-500">{t("compare_sub")}</p>

      <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-navy">{t("policy_a")}</label><div className="mt-1.5"><DocPicker testId="cmp-a" value={a} onChange={setA} exclude={b} placeholder={t("policy_a_ph")} /></div></div>
          <div><label className="text-sm font-medium text-navy">{t("policy_b")}</label><div className="mt-1.5"><DocPicker testId="cmp-b" value={b} onChange={setB} exclude={a} placeholder={t("policy_b_ph")} /></div></div>
        </div>
        <div className="mt-4 max-w-sm">
          <label className="text-sm font-medium text-navy">{t("what_matters")}</label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="cmp-priority" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.v} value={p.v}>{t(p.labelKey)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="cmp-run" onClick={run} disabled={loading} className="mt-4 rounded-full brand-gradient text-white">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("comparing")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("compare_policies")}</>}
        </Button>
      </div>

      {result && (
        <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("side_by_side")}</h2>
            <div className="flex gap-2">
              <Button data-testid="cmp-save" variant="outline" onClick={saveCmp} className="rounded-full"><Save className="h-4 w-4 mr-2" />{t("save")}</Button>
              <Button data-testid="cmp-export" variant="outline" onClick={exportPdf} className="rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_pdf")}</Button>
            </div>
          </div>
          <div className="mt-3"><AIText>{result.result}</AIText></div>
        </div>
      )}
    </AppLayout>
  );
}
